/**
 * IpoManagementCard — Admin-only card for the Data Sources page.
 *
 * Handles the IPO admin flow:
 *   1. Create a new IPO scaffold  (symbol / company / sector / dates / price)
 *   2. Upload the prospectus PDF   → triggers Claude analysis pipeline
 *   3. Re-analyse an existing IPO  → reuses the stored PDF
 *   4. Update status / post-listing prices
 *   5. Delete a failed or stale IPO row
 *
 * Reads `/ipo/list` for the table data. Writes are via the admin-only
 * `/admin/ipo/...` endpoints. Status badges match the IPO page theme so
 * admins see consistent signals everywhere.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiDelete, api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Rocket, Upload, RefreshCcw, Trash2, ExternalLink, Pencil, Search } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  pending_prospectus: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  analysing: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40",
  analysed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  partial: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  failed: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const StatusBadge = ({ status }) => {
  const cls = STATUS_COLOR[status] || "bg-muted text-muted-foreground";
  const label = {
    pending_prospectus: "Awaiting prospectus",
    analysing: "Analysing…",
    analysed: "Analysed",
    partial: "Partial",
    failed: "Failed",
  }[status] || status;
  return (
    <Badge className={cls} variant="outline" data-testid={`ipo-status-${status}`}>{label}</Badge>
  );
};

// ---------------- Edit IPO details dialog (Bug 1 fix) ----------------
const SECTOR_OPTIONS = [
  "Banks", "Beverage, Food & Tobacco", "Capital Goods", "Chemicals",
  "Consumer Services", "Diversified Financials", "Energy",
  "Food, Beverage & Tobacco", "Health Care Equipment & Services",
  "Hotels, Restaurants & Leisure", "Household & Personal Products",
  "Insurance", "Manufacturing", "Materials", "Real Estate",
  "Retailing", "Software & Services", "Telecommunication Services",
  "Transportation", "Utilities", "Other",
];

// Small helper — turn an ISO datetime back into YYYY-MM-DD for <input type=date>.
const toDateInput = (val) => {
  if (!val) return "";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch { return ""; }
};

const EditIpoDialog = ({ ipo, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const det = ipo?.ipo_details || {};
  const [form, setForm] = useState({
    company_name: ipo?.company_name || "",
    sector: ipo?.sector || "",
    ipo_price: det.ipo_price ?? "",
    ipo_price_min: det.ipo_price_min ?? "",
    ipo_price_max: det.ipo_price_max ?? "",
    subscription_opens: toDateInput(det.subscription_opens),
    subscription_closes: toDateInput(det.subscription_closes),
    listing_date: toDateInput(det.listing_date),
    ipo_status: det.ipo_status || "upcoming",
    use_of_proceeds: det.use_of_proceeds || "",
  });
  // Reset local form state every time the dialog re-opens so stale edits
  // from a cancelled dialog don't leak into the next open.
  useEffect(() => {
    if (!open) return;
    setForm({
      company_name: ipo?.company_name || "",
      sector: ipo?.sector || "",
      ipo_price: det.ipo_price ?? "",
      ipo_price_min: det.ipo_price_min ?? "",
      ipo_price_max: det.ipo_price_max ?? "",
      subscription_opens: toDateInput(det.subscription_opens),
      subscription_closes: toDateInput(det.subscription_closes),
      listing_date: toDateInput(det.listing_date),
      ipo_status: det.ipo_status || "upcoming",
      use_of_proceeds: det.use_of_proceeds || "",
    });
  }, [open, ipo]);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name.trim() || null,
        sector: form.sector || null,
        ipo_price: form.ipo_price === "" ? null : parseFloat(form.ipo_price),
        ipo_price_min: form.ipo_price_min === "" ? null : parseFloat(form.ipo_price_min),
        ipo_price_max: form.ipo_price_max === "" ? null : parseFloat(form.ipo_price_max),
        subscription_opens: form.subscription_opens || null,
        subscription_closes: form.subscription_closes || null,
        listing_date: form.listing_date || null,
        ipo_status: form.ipo_status || null,
        use_of_proceeds: form.use_of_proceeds.trim() || null,
      };
      // Only send non-null values so the PUT is a true partial update.
      const body = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== ""));
      await api.put(`/admin/ipo/${encodeURIComponent(ipo.symbol)}`, body);
      toast.success(`${ipo.symbol} details updated`);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`ipo-edit-${ipo.symbol}`}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid={`ipo-edit-dialog-${ipo.symbol}`}>
        <DialogHeader>
          <DialogTitle>Edit IPO Details — {ipo.symbol}</DialogTitle>
          <DialogDescription>
            Correct any IPO metadata (name, sector, price, dates, status) without
            re-running the Claude analysis. Changes to <strong>IPO price</strong> automatically
            re-derive PE / PB / Graham ratios on save.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="md:col-span-2">
            <Label className="text-xs">Company name</Label>
            <Input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              data-testid={`ipo-edit-company-${ipo.symbol}`}
            />
          </div>
          <div>
            <Label className="text-xs">Sector</Label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              data-testid={`ipo-edit-sector-${ipo.symbol}`}
            >
              <option value="">— Select sector —</option>
              {SECTOR_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">IPO Status</Label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={form.ipo_status}
              onChange={(e) => setForm({ ...form, ipo_status: e.target.value })}
              data-testid={`ipo-edit-status-${ipo.symbol}`}
            >
              <option value="upcoming">Upcoming</option>
              <option value="open">Open for subscription</option>
              <option value="closed">Closed (pending listing)</option>
              <option value="listed">Listed</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">IPO Price (Rs.)</Label>
            <Input
              type="number" step="0.01" value={form.ipo_price}
              onChange={(e) => setForm({ ...form, ipo_price: e.target.value })}
              data-testid={`ipo-edit-price-${ipo.symbol}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Min price</Label>
              <Input
                type="number" step="0.01" value={form.ipo_price_min}
                onChange={(e) => setForm({ ...form, ipo_price_min: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Max price</Label>
              <Input
                type="number" step="0.01" value={form.ipo_price_max}
                onChange={(e) => setForm({ ...form, ipo_price_max: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Subscription opens</Label>
            <Input
              type="date" value={form.subscription_opens}
              onChange={(e) => setForm({ ...form, subscription_opens: e.target.value })}
              data-testid={`ipo-edit-sub-opens-${ipo.symbol}`}
            />
          </div>
          <div>
            <Label className="text-xs">Subscription closes</Label>
            <Input
              type="date" value={form.subscription_closes}
              onChange={(e) => setForm({ ...form, subscription_closes: e.target.value })}
              data-testid={`ipo-edit-sub-closes-${ipo.symbol}`}
            />
          </div>
          <div>
            <Label className="text-xs">Listing date</Label>
            <Input
              type="date" value={form.listing_date}
              onChange={(e) => setForm({ ...form, listing_date: e.target.value })}
              data-testid={`ipo-edit-listing-${ipo.symbol}`}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Use of proceeds (optional)</Label>
            <Input
              value={form.use_of_proceeds}
              onChange={(e) => setForm({ ...form, use_of_proceeds: e.target.value })}
              placeholder="60% capacity expansion, 30% debt reduction, 10% working capital."
              data-testid={`ipo-edit-proceeds-${ipo.symbol}`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid={`ipo-edit-save-${ipo.symbol}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ---------------- Add New IPO dialog ----------------
const AddIpoDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    symbol: "", company_name: "", sector: "", ipo_price: "",
    subscription_opens: "", subscription_closes: "", listing_date: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.symbol || !form.symbol.includes(".")) {
      toast.error("CSE symbol is required (e.g. JXG.N0000)");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/admin/ipo/create", {
        symbol: form.symbol.trim(),
        company_name: form.company_name.trim() || undefined,
        sector: form.sector.trim() || undefined,
        ipo_price: form.ipo_price ? parseFloat(form.ipo_price) : undefined,
        subscription_opens: form.subscription_opens || undefined,
        subscription_closes: form.subscription_closes || undefined,
        listing_date: form.listing_date || undefined,
      });
      toast.success(`IPO ${form.symbol} registered. Upload the prospectus to start analysis.`);
      setOpen(false);
      setForm({
        symbol: "", company_name: "", sector: "", ipo_price: "",
        subscription_opens: "", subscription_closes: "", listing_date: "",
      });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to register IPO");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="default" className="gap-1.5" data-testid="ipo-add-button">
          <Plus className="h-3.5 w-3.5" /> Add New IPO
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="ipo-add-dialog">
        <DialogHeader>
          <DialogTitle>Register new IPO</DialogTitle>
          <DialogDescription>
            Scaffold an IPO record so it appears on the admin list. Upload the prospectus
            afterwards to trigger the Claude analysis pipeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">CSE symbol *</Label>
              <Input
                value={form.symbol}
                onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                placeholder="JXG.N0000"
                data-testid="ipo-add-symbol"
              />
            </div>
            <div>
              <Label className="text-xs">Company name</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="JXG Company PLC"
                data-testid="ipo-add-company"
              />
            </div>
            <div>
              <Label className="text-xs">Sector</Label>
              <Input
                value={form.sector}
                onChange={(e) => setForm({ ...form, sector: e.target.value })}
                placeholder="Manufacturing"
                data-testid="ipo-add-sector"
              />
            </div>
            <div>
              <Label className="text-xs">IPO price (Rs.)</Label>
              <Input
                type="number" step="0.01"
                value={form.ipo_price}
                onChange={(e) => setForm({ ...form, ipo_price: e.target.value })}
                placeholder="15.00"
                data-testid="ipo-add-price"
              />
            </div>
            <div>
              <Label className="text-xs">Subscription opens</Label>
              <Input
                type="date"
                value={form.subscription_opens}
                onChange={(e) => setForm({ ...form, subscription_opens: e.target.value })}
                data-testid="ipo-add-sub-opens"
              />
            </div>
            <div>
              <Label className="text-xs">Subscription closes</Label>
              <Input
                type="date"
                value={form.subscription_closes}
                onChange={(e) => setForm({ ...form, subscription_closes: e.target.value })}
                data-testid="ipo-add-sub-closes"
              />
            </div>
            <div>
              <Label className="text-xs">Listing date</Label>
              <Input
                type="date"
                value={form.listing_date}
                onChange={(e) => setForm({ ...form, listing_date: e.target.value })}
                data-testid="ipo-add-listing-date"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ipo-add-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Register IPO
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Upload prospectus dialog ----------------
const DOC_TYPE_OPTIONS = [
  { value: "prospectus", label: "Prospectus (main)", Icon: Upload },
  { value: "financial_statements", label: "Financial Statements" },
  { value: "accountants_report", label: "Accountant's Report / 5yr Summary" },
  { value: "valuation_report", label: "Valuation Report" },
  { value: "other", label: "Other supporting document" },
];

const DOC_TYPE_LABELS = Object.fromEntries(DOC_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const UploadProspectusDialog = ({ symbol, onUploaded, existingDocTypes = [] }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("prospectus");
  const [busy, setBusy] = useState(false);

  // If the admin already uploaded the main prospectus, pre-select the
  // first missing supporting doc type so the workflow feels natural.
  useEffect(() => {
    if (!open) return;
    const prioritised = ["prospectus", "financial_statements", "accountants_report", "valuation_report", "other"];
    const nextMissing = prioritised.find((t) => !existingDocTypes.includes(t));
    if (nextMissing) setDocType(nextMissing);
  }, [open, existingDocTypes]);

  const submit = async () => {
    if (!file) { toast.error("Pick a PDF to upload"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ipo_document_type", docType);
      fd.append("auto_analyse", "false"); // admin triggers analysis manually
      await api.post(`/admin/ipo/${encodeURIComponent(symbol)}/upload-prospectus`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_TYPE_LABELS[docType]} uploaded for ${symbol}`, {
        description: "Click ‘Run Full Analysis’ once every supporting document is uploaded.",
        duration: 6000,
      });
      setOpen(false);
      setFile(null);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`ipo-upload-${symbol}`}>
          <Upload className="h-3.5 w-3.5" /> Upload Document
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`ipo-upload-dialog-${symbol}`}>
        <DialogHeader>
          <DialogTitle>Upload an IPO document for {symbol}</DialogTitle>
          <DialogDescription>
            Claude merges the extraction from every uploaded document when you click
            ‘Run Full Analysis’. Tag each PDF with the right type so financial
            numbers from the audited statements override summary numbers in the prospectus.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Document type</Label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              data-testid={`ipo-upload-type-${symbol}`}
            >
              {DOC_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                  {existingDocTypes.includes(o.value) ? " (already uploaded — will add another)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">PDF file</Label>
            <Input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid={`ipo-upload-file-${symbol}`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid={`ipo-upload-submit-${symbol}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Per-IPO documents row (inline expandable section) ----------------
const IpoDocumentList = ({ symbol }) => {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet(`/admin/ipo/${encodeURIComponent(symbol)}/documents`);
      setDocs(res?.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load documents");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (reportId) => {
    if (!window.confirm("Delete this IPO document? This removes the PDF from disk.")) return;
    try {
      await apiDelete(`/admin/ipo/${encodeURIComponent(symbol)}/documents/${encodeURIComponent(reportId)}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading || docs === null) {
    return (
      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading documents…
      </div>
    );
  }

  const existingDocTypes = docs.map((d) => d.ipo_document_type);

  return (
    <div className="space-y-1.5" data-testid={`ipo-docs-list-${symbol}`}>
      {docs.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-2 text-[11px] text-muted-foreground">
          No documents yet. Upload the main prospectus + any supporting PDFs (audited
          financial statements, accountant's report, valuation report).
        </div>
      ) : (
        <div className="divide-y rounded-md border">
          {docs.map((d) => (
            <div
              key={d.report_id}
              className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs"
              data-testid={`ipo-doc-row-${d.report_id}`}
            >
              <div className="min-w-0 flex items-center gap-2">
                {d.is_analysed ? (
                  <span className="text-[hsl(var(--success))]" title="Included in last analysis">✓</span>
                ) : (
                  <span className="text-muted-foreground" title="Pending analysis">•</span>
                )}
                <Badge variant="outline" className="text-[10px]">
                  {DOC_TYPE_LABELS[d.ipo_document_type] || d.ipo_document_type}
                </Badge>
                <span className="truncate" title={d.file_name}>{d.file_name}</span>
                {d.size_bytes && (
                  <span className="text-muted-foreground text-[10px]">
                    · {(d.size_bytes / 1024 / 1024).toFixed(1)} MB
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(d.report_id)}
                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                data-testid={`ipo-doc-delete-${d.report_id}`}
                title="Delete document"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <UploadProspectusDialog symbol={symbol} onUploaded={load} existingDocTypes={existingDocTypes} />
        {docs.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {docs.length} document{docs.length === 1 ? "" : "s"} uploaded
          </span>
        )}
      </div>
    </div>
  );
};

// ---------------- Update status dialog ----------------
const UpdateStatusDialog = ({ ipo, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    ipo_status: ipo?.ipo_details?.ipo_status || "upcoming",
    listing_price: ipo?.post_listing_tracking?.listing_price || "",
    current_price: ipo?.post_listing_tracking?.current_price || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await apiPost("/admin/ipo/update-status", {
        symbol: ipo.symbol,
        ipo_status: form.ipo_status,
        listing_price: form.listing_price ? parseFloat(form.listing_price) : null,
        current_price: form.current_price ? parseFloat(form.current_price) : null,
      });
      toast.success(`${ipo.symbol} status updated`);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`ipo-status-update-${ipo.symbol}`}>
          <RefreshCcw className="h-3.5 w-3.5" /> Update Status
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update status for {ipo.symbol}</DialogTitle>
          <DialogDescription>
            Flip the IPO lifecycle stage and record post-listing prices so the
            track record accuracy updates.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Status</Label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={form.ipo_status}
              onChange={(e) => setForm({ ...form, ipo_status: e.target.value })}
              data-testid={`ipo-status-select-${ipo.symbol}`}
            >
              <option value="upcoming">Upcoming</option>
              <option value="open">Open for subscription</option>
              <option value="closed">Closed (pending listing)</option>
              <option value="listed">Listed</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Listing price (Rs.)</Label>
              <Input
                type="number" step="0.01"
                value={form.listing_price}
                onChange={(e) => setForm({ ...form, listing_price: e.target.value })}
                placeholder="18.50"
                data-testid={`ipo-listing-price-${ipo.symbol}`}
              />
            </div>
            <div>
              <Label className="text-xs">Current price (Rs.)</Label>
              <Input
                type="number" step="0.01"
                value={form.current_price}
                onChange={(e) => setForm({ ...form, current_price: e.target.value })}
                placeholder="21.00"
                data-testid={`ipo-current-price-${ipo.symbol}`}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid={`ipo-status-submit-${ipo.symbol}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Main card ----------------
const IpoManagementCard = () => {
  const [ipos, setIpos] = useState([]);
  const [loading, setLoading] = useState(true);
  // jobStatuses[symbol] = { status, progress: {...}, has_job, updated_at }
  const [jobStatuses, setJobStatuses] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const buckets = await apiGet("/ipo/list");
      const rows = [
        ...(buckets.open || []),
        ...(buckets.upcoming || []),
        ...(buckets.recently_listed || []),
        ...(buckets.historical || []),
        ...(buckets.pending_prospectus || []),
      ];
      // Dedupe by symbol (a row can live in pending_prospectus but also
      // reappear in historical after an admin flips status — keep the
      // more-advanced state).
      const seen = new Set();
      const deduped = [];
      for (const r of rows) {
        if (seen.has(r.symbol)) continue;
        seen.add(r.symbol);
        deduped.push(r);
      }
      setIpos(deduped);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load IPOs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---------------- Live progress polling ----------------
  // While ANY IPO is in `analysing` state (or has an active job row) we
  // poll `/admin/ipo/{symbol}/status` every 5 s so the admin sees
  // "Analysing 2/4 — Main Prospectus" in real time instead of an
  // anonymous spinner. Stops polling automatically once every symbol is
  // either `complete`, `failed`, or `idle`.
  useEffect(() => {
    const activeSymbols = ipos
      .filter((x) => {
        if (x.status === "analysing") return true;
        // Keep polling while job status is running even if the ipo row
        // hasn't refreshed yet.
        const s = jobStatuses[x.symbol]?.status;
        return s === "running";
      })
      .map((x) => x.symbol);

    if (activeSymbols.length === 0) return undefined;

    let cancelled = false;
    const tick = async () => {
      const results = await Promise.all(
        activeSymbols.map(async (sym) => {
          try {
            const r = await apiGet(`/admin/ipo/${encodeURIComponent(sym)}/status`);
            return [sym, r];
          } catch (e) {
            return [sym, null];
          }
        }),
      );
      if (cancelled) return;
      setJobStatuses((prev) => {
        const next = { ...prev };
        let shouldReload = false;
        for (const [sym, r] of results) {
          if (!r) continue;
          const prevStatus = prev[sym]?.status;
          next[sym] = r;
          // If a run just finished, refresh the IPO list so the
          // row's `status`, scoring, and analysed_at update.
          if (prevStatus === "running" && r.status && r.status !== "running") {
            shouldReload = true;
          }
        }
        if (shouldReload) {
          // Defer the reload so React can commit this state first.
          setTimeout(load, 200);
        }
        return next;
      });
    };

    // Fire immediately, then poll every 5 s.
    tick();
    const handle = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // We intentionally depend on the list of active symbols (stringified)
    // so we re-subscribe when a new IPO enters/exits `analysing`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipos.map((x) => `${x.symbol}:${x.status}`).join("|")]);

  const reanalyse = async (symbol) => {
    try {
      const res = await apiPost(`/admin/ipo/analyse/${encodeURIComponent(symbol)}`);
      toast.success(`Full analysis started for ${symbol}`, {
        description: `Merging ${res?.documents || "all"} document${res?.documents === 1 ? "" : "s"}. Screener score will auto-refresh when complete.`,
        duration: 6000,
      });
      // Seed the progress tracker so the button flips to "Analysing…"
      // immediately, before the first poll tick.
      setJobStatuses((prev) => ({
        ...prev,
        [symbol]: {
          status: "running",
          progress: {
            documents_total: res?.documents || 1,
            documents_processed: 0,
            current_document: null,
            current_phase: "starting",
          },
        },
      }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed to start");
    }
  };

  const remove = async (symbol) => {
    if (!window.confirm(`Delete the IPO record for ${symbol}? This removes the analysis row but NOT the stored prospectus PDF.`)) return;
    try {
      await apiDelete(`/admin/ipo/${encodeURIComponent(symbol)}`);
      toast.success(`${symbol} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---------------- Perplexity web research ----------------
  // researchStates[symbol] = { running: bool, lastOk: bool, sources: int }
  const [researchStates, setResearchStates] = useState({});

  const researchCompany = async (symbol) => {
    try {
      setResearchStates((p) => ({ ...p, [symbol]: { running: true } }));
      await apiPost(`/admin/ipo/${encodeURIComponent(symbol)}/research`);
      toast.success(`Web research started for ${symbol}`, {
        description: "Perplexity is searching for subsidiaries, Deloitte valuation, oversubscription data… takes ~30-60 s.",
        duration: 6000,
      });
      // Poll every 5 s until finished (status = complete | failed)
      const poll = async () => {
        try {
          const r = await apiGet(`/admin/ipo/${encodeURIComponent(symbol)}/research`);
          const wr = r?.web_research || {};
          if (wr.status === "complete" || wr.status === "failed") {
            setResearchStates((p) => ({
              ...p, [symbol]: {
                running: false,
                lastOk: wr.status === "complete",
                sources: (wr.sources || []).length,
                queries: wr.queries_succeeded,
                total: wr.queries_total,
                cost: wr.cost_usd,
              },
            }));
            if (wr.status === "complete") {
              toast.success(`Research complete for ${symbol}`, {
                description: `${wr.queries_succeeded}/${wr.queries_total} queries · ${(wr.sources || []).length} sources · $${(wr.cost_usd || 0).toFixed(3)}`,
              });
            } else {
              toast.error(`Research failed for ${symbol}`, {
                description: (wr.error || "").slice(0, 120),
              });
            }
            load();
            return;
          }
          // Still running — keep polling
          setTimeout(poll, 5000);
        } catch (e) {
          setResearchStates((p) => ({ ...p, [symbol]: { running: false } }));
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setResearchStates((p) => ({ ...p, [symbol]: { running: false } }));
      toast.error(e?.response?.data?.detail || "Failed to start research");
    }
  };

  const counts = {
    active: ipos.filter((x) => ["upcoming", "open"].includes((x.ipo_details?.ipo_status || "").toLowerCase())).length,
    analysed: ipos.filter((x) => x.status === "analysed").length,
    pending: ipos.filter((x) => x.status === "pending_prospectus").length,
  };

  return (
    <Card id="ipo" data-testid="ipo-management-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-primary" />
            <div>
              <CardTitle className="text-base">IPO Management</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Register CSE IPOs, upload prospectus PDFs, and trigger the
                Claude analysis pipeline.
              </p>
            </div>
          </div>
          <AddIpoDialog onCreated={load} />
        </div>
        <div className="flex items-center gap-3 mt-3 text-xs">
          <Badge variant="secondary" data-testid="ipo-count-active">Active: {counts.active}</Badge>
          <Badge variant="secondary" data-testid="ipo-count-analysed">Analysed: {counts.analysed}</Badge>
          <Badge variant="secondary" data-testid="ipo-count-pending">Pending: {counts.pending}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : ipos.length === 0 ? (
          <div className="rounded-md border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
            No IPOs registered yet. Click <strong>Add New IPO</strong> to get started.
          </div>
        ) : (
          <div className="divide-y">
            {ipos.map((ipo) => (
              <div
                key={ipo.symbol}
                className="py-3 space-y-2"
                data-testid={`ipo-row-${ipo.symbol}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm">{ipo.symbol}</span>
                      <StatusBadge status={ipo.status} />
                      {ipo.ipo_details?.ipo_status && (
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {ipo.ipo_details.ipo_status}
                        </Badge>
                      )}
                      {ipo.ipo_recommendation?.signal && (
                        <Badge variant="outline" className="text-[10px]">
                          ★ {ipo.ipo_recommendation.signal}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm font-medium">{ipo.company_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {ipo.sector || "—"} · {ipo.ipo_details?.ipo_price ? `Rs. ${ipo.ipo_details.ipo_price}` : "price —"}
                    </div>
                    {/* Document status — one line per uploaded IPO doc
                        so admins can see at a glance which pieces the
                        analysis has consumed. Maps each
                        `pipeline.documents_consumed[].ipo_document_type`
                        to a ✓/⏳/✗ mark derived from whether its
                        corresponding chunk appears in
                        `pipeline.chunks_succeeded`. */}
                    {(() => {
                      const docs = ipo.pipeline?.documents_consumed || [];
                      if (docs.length === 0) return null;
                      const succeededChunks = new Set(
                        ipo.pipeline?.chunks_succeeded || [],
                      );
                      const failedChunks = new Set(
                        (ipo.pipeline?.chunks_failed || []).map((f) => String(f).split(":")[0]),
                      );
                      const visionDocs = new Set(
                        ipo.pipeline?.vision_documents || [],
                      );
                      const DOC_ROUTE = {
                        prospectus: ["business_overview", "ipo_details"],
                        financial_statements: ["financials"],
                        accountants_report: ["financials"],
                        valuation_report: ["valuation_extra"],
                        other: [],
                      };
                      return (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {docs.map((d, i) => {
                            const type = d.ipo_document_type || "other";
                            const label = type.replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase());
                            const routes = DOC_ROUTE[type] || [];
                            const any = routes.some((lbl) => succeededChunks.has(lbl));
                            const anyFailed = routes.some((lbl) => failedChunks.has(lbl));
                            const isVision = visionDocs.has(type);
                            let icon = "✓";
                            let tone = "text-[hsl(var(--success))] bg-[hsl(var(--success))]/8 border-[hsl(var(--success))]/30";
                            let title = `${label} — analysed`;
                            if (routes.length === 0 || (!any && !anyFailed)) {
                              icon = "·";
                              tone = "text-muted-foreground bg-muted/40";
                              title = `${label} — uploaded, no routed chunks`;
                            } else if (!any && anyFailed) {
                              icon = "✗";
                              tone = "text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/8 border-[hsl(var(--destructive))]/30";
                              title = `${label} — extraction failed`;
                            } else if (isVision) {
                              title = `${label} — analysed via Claude Vision (scanned PDF)`;
                            }
                            return (
                              <span
                                key={i}
                                className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}
                                title={title}
                                data-testid={`ipo-docstatus-${ipo.symbol}-${type}`}
                              >
                                {icon} {label}
                                {isVision && <span className="ml-0.5">📷</span>}
                              </span>
                            );
                          })}
                          {ipo.status === "partial" && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))]"
                              title="Coordinator produced no recommendation — re-run to complete specialist views"
                            >
                              ⚠ Specialist views pending
                            </span>
                          )}
                          {ipo.web_research?.status === "complete" && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded border bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/30 text-[hsl(var(--info))]"
                              title={`Perplexity research: ${ipo.web_research.queries_succeeded || 0}/${ipo.web_research.queries_total || 0} queries · ${(ipo.web_research.sources || []).length} sources · $${(ipo.web_research.cost_usd || 0).toFixed(3)}`}
                            >
                              🔍 Research · {(ipo.web_research.sources || []).length} sources
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <Button
                      asChild
                      size="sm"
                      variant="ghost"
                      className="gap-1.5"
                      data-testid={`ipo-view-${ipo.symbol}`}
                    >
                      <Link to={`/ipo/${ipo.symbol}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </Link>
                    </Button>
                    <EditIpoDialog ipo={ipo} onUpdated={load} />
                    {(() => {
                      const rs = researchStates[ipo.symbol];
                      const researched = ipo.web_research?.status === "complete"
                        || (ipo.web_research?.queries_succeeded || 0) > 0;
                      const running = !!rs?.running
                        || ipo.web_research?.status === "researching";
                      let label = researched ? "Re-research" : "Research Online";
                      if (running) label = "Researching…";
                      return (
                        <Button
                          size="sm"
                          variant={researched ? "outline" : "secondary"}
                          onClick={() => researchCompany(ipo.symbol)}
                          disabled={running}
                          className="gap-1.5"
                          data-testid={`ipo-research-online-${ipo.symbol}`}
                          title={researched
                            ? `Re-run Perplexity web research. Last run: ${ipo.web_research?.queries_succeeded || 0}/${ipo.web_research?.queries_total || 0} queries, ${(ipo.web_research?.sources || []).length} sources.`
                            : "Run Perplexity sonar-pro web research to find subsidiaries, independent valuation, oversubscription data, and listing context before the full analysis."
                          }
                        >
                          {running
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                            : <Search className="h-3.5 w-3.5 shrink-0" />}
                          <span className="truncate">{label}</span>
                        </Button>
                      );
                    })()}
                    {(() => {
                      // Derive live progress for this row. Prefer the
                      // polled job status (fresher) over the static
                      // `ipo.status` which only refreshes via /ipo/list.
                      const job = jobStatuses[ipo.symbol];
                      const jobRunning = job?.status === "running";
                      const isAnalysing = ipo.status === "analysing" || jobRunning;
                      const prog = job?.progress;
                      let progLabel = "Run Full Analysis";
                      if (isAnalysing) {
                        if (prog?.current_phase === "coordinator") {
                          progLabel = "Finalising…";
                        } else if (prog?.documents_total) {
                          const done = prog.documents_processed || 0;
                          const total = prog.documents_total;
                          const cur = prog.current_document
                            ? ` · ${prog.current_document}`
                            : "";
                          progLabel = `Analysing ${done}/${total}${cur}`;
                        } else {
                          progLabel = "Analysing…";
                        }
                      }
                      return (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => reanalyse(ipo.symbol)}
                          disabled={isAnalysing}
                          className="gap-1.5 max-w-[260px]"
                          data-testid={`ipo-run-full-analysis-${ipo.symbol}`}
                          title={isAnalysing
                            ? `Progress: ${prog?.current_phase || "running"}`
                            : "Merge every uploaded IPO document and run the full Claude analysis pipeline."
                          }
                        >
                          {isAnalysing
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                            : <RefreshCcw className="h-3.5 w-3.5 shrink-0" />}
                          <span className="truncate">{progLabel}</span>
                        </Button>
                      );
                    })()}
                    <UpdateStatusDialog ipo={ipo} onUpdated={load} />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(ipo.symbol)}
                      className="gap-1.5 text-destructive hover:text-destructive"
                      data-testid={`ipo-delete-${ipo.symbol}`}
                      title="Delete the entire IPO record (documents remain in company_reports)."
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {/* Per-IPO document list — expand inline so admins always see
                    which PDFs are on file and can upload supporting docs
                    without leaving this page. */}
                <div className="pl-1">
                  <IpoDocumentList symbol={ipo.symbol} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IpoManagementCard;
